import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Batch } from "./models/batch.model";
import { Observable } from "rxjs";
import { environment } from "environments/environment";

@Injectable({
  providedIn: "root",
})
export class BatchService {
  private readonly baseUrl = environment.apiUrl + "/batches";

  constructor(private http: HttpClient) {}
  getAllBatches(): Observable<Batch[]> {
    return this.http.get<Batch[]>(this.baseUrl);
  }

  getByItemId(itemId: string, page?: number, limit?: number, storeId?: string | null): Observable<Batch[] | { data: Batch[]; totalRecords: number; totalPages: number; currentPage: number; limit: number }> {
    let params = new HttpParams();
    if (page) params = params.set('page', page.toString());
    if (limit) params = params.set('limit', limit.toString());
    if (storeId) params = params.set('storeId', storeId);

    return this.http.get<Batch[] | { data: Batch[]; totalRecords: number; totalPages: number; currentPage: number; limit: number }>(`${this.baseUrl}/item/${itemId}`, { params });
  }

  getById(id: string): Observable<Batch> {
    return this.http.get<Batch>(`${this.baseUrl}/${id}`);
  }

  create(batch: Partial<Batch>): Observable<Batch> {
    return this.http.post<Batch>(this.baseUrl, batch);
  }

  update(id: string, batch: Partial<Batch>): Observable<Batch> {
    return this.http.patch<Batch>(`${this.baseUrl}/${id}`, batch);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  checkDuplicate(
    itemId: string,
    batchNo: string,
    excludeId?: string
  ): Observable<{ exists: boolean }> {
    const params = new HttpParams()
      .set("itemId", itemId)
      .set("batchNo", batchNo)
      .set("excludeId", excludeId || "");

    return this.http.get<{ exists: boolean }>(
      `${this.baseUrl}/check-duplicate`,
      { params }
    );
  }

  getFifoBatches(itemId: string, quantity: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/item/${itemId}/fifo-split`, {
      params: { qty: quantity.toString() },
    });
  }

  saveBatchAdjustments(batchId: string, adjustmentData: any, storeId: string): Observable<Batch> {
    // Map the form data to the batch update format
    // Note: purchaseRate is not stored in Batch table, so we exclude it
    const updateData: any = {};
    
    // Only include fields that have actual values to avoid sending undefined
    if (adjustmentData.batchNo !== undefined && adjustmentData.batchNo !== null) {
      updateData.batchNo = adjustmentData.batchNo;
    }
    if (adjustmentData.expiryDate !== undefined && adjustmentData.expiryDate !== null) {
      // Ensure date is in ISO format
      updateData.expiryDate = adjustmentData.expiryDate instanceof Date ? 
        adjustmentData.expiryDate.toISOString() : adjustmentData.expiryDate;
    }
    if (adjustmentData.mrp !== undefined && adjustmentData.mrp !== null) {
      updateData.mrp = parseFloat(adjustmentData.mrp) || 0;
    }
    if (adjustmentData.quantity !== undefined && adjustmentData.quantity !== null) {
      const q = Number(adjustmentData.quantity);
      if (Number.isFinite(q)) {
        // Backend saveBatchAdjustments reads `quantity`; do not send only currentStock
        updateData.quantity = q;
        updateData.currentStock = q;
      }
    }
    
    // Include the new quantity breakdown fields for FIFO processing
    if (adjustmentData.newPurchaseQuantity !== undefined && adjustmentData.newPurchaseQuantity !== null) {
      updateData.newPurchaseQuantity = parseFloat(adjustmentData.newPurchaseQuantity) || 0;
    }
    if (adjustmentData.newFreeQuantity !== undefined && adjustmentData.newFreeQuantity !== null) {
      updateData.newFreeQuantity = parseFloat(adjustmentData.newFreeQuantity) || 0;
    }
    if (adjustmentData.originalPurchaseQuantity !== undefined && adjustmentData.originalPurchaseQuantity !== null) {
      updateData.originalPurchaseQuantity = parseFloat(adjustmentData.originalPurchaseQuantity) || 0;
    }
    if (adjustmentData.originalFreeQuantity !== undefined && adjustmentData.originalFreeQuantity !== null) {
      updateData.originalFreeQuantity = parseFloat(adjustmentData.originalFreeQuantity) || 0;
    }
    if (adjustmentData.originalTotalQuantity !== undefined && adjustmentData.originalTotalQuantity !== null) {
      updateData.originalTotalQuantity = parseFloat(adjustmentData.originalTotalQuantity) || 0;
    }
    
    // Pricing fields (PurchaseItem + inventory); W.S./Spl. stay on inventory layer
    const setNum = (key: string, value: unknown) => {
      if (value !== undefined && value !== null) {
        updateData[key] = parseFloat(String(value)) || 0;
      }
    };
    setNum('purchaseRate', adjustmentData.purchaseRate);
    setNum('saleRate', adjustmentData.saleRate);
    setNum('wsRate', adjustmentData.wsRate);
    setNum('splRate', adjustmentData.splRate); // special rate 1
    setNum('purchaseDiscount', adjustmentData.purchaseDiscount);
    
      // Hold/Break/Expiry status (will be stored in inventory table)
      if (adjustmentData.holdBreakExpiry !== undefined && adjustmentData.holdBreakExpiry !== null) {
        updateData.holdBreakExpiry = adjustmentData.holdBreakExpiry;
      }
    
    if (adjustmentData.gstPercent !== undefined && adjustmentData.gstPercent !== null) {
      const gstPercent = parseFloat(adjustmentData.gstPercent) || 0;
      updateData.cgstPercent = gstPercent / 3;
      updateData.sgstPercent = gstPercent / 3;
      updateData.igstPercent = 0;
      updateData.utgstPercent = 0;
    }

    // Use the proper batches endpoint for quantity adjustments with FIFO logic
    return this.http.post<Batch>(`${environment.apiUrl}/batches/${batchId}/adjustments?storeId=${storeId}`, updateData);
  }
}
